import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { DiamondMark } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { mainSiteHref, goSite } from "@/lib/site";

const LINKS = [
  ["Wire", "/wire", "datanav-wire"],
  ["Terminal", "/terminal", "datanav-terminal"],
  ["Calendar", "/calendar", "datanav-calendar"],
  ["Africa News", "/africa-news", "datanav-africanews"],
  ["Metals", "/metals", "datanav-metals"],
  ["Data", "/vdr-rdi", "datanav-data"],
  ["Pricing", "/pricing", "datanav-pricing"],
  ["About", "/about", "datanav-about"],
];

export function DataLogo({ onClick }) {
  return (
    <button onClick={onClick} data-testid="datanav-logo" className="flex items-center gap-2.5 text-left">
      <DiamondMark size={22} />
      <span className="leading-none">
        <span className="block font-serif text-[1.05rem] tracking-[0.14em] text-bb-text uppercase">Virtus <span className="text-bb-gold">Data</span></span>
        <span className="block text-[0.5rem] uppercase tracking-[0.26em] text-bb-dim mt-1">An Imprint of Virtus Registry</span>
      </span>
    </button>
  );
}

export function DataNav() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const go = (to) => { setOpen(false); nav(to); };
  return (
    <header className="fixed top-0 inset-x-0 z-40 bb-glass border-b border-white/5">
      <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
        <DataLogo onClick={() => nav("/")} />
        <nav className="hidden md:flex items-center gap-7">
          {LINKS.map(([label, to, tid]) => (
            <button key={to} onClick={() => nav(to)} data-testid={tid}
              className="text-[0.72rem] uppercase tracking-[0.18em] text-bb-muted hover:text-bb-gold transition-colors">
              {label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          {user ? (
            <>
              <button onClick={logout} data-testid="datanav-logout"
                className="hidden sm:block text-sm text-bb-text/80 hover:text-bb-gold transition-colors px-2 py-2">Sign Out</button>
              <button onClick={() => nav("/dashboard")} data-testid="datanav-dashboard"
                className="hidden sm:block bg-bb-gold text-bb-bg hover:bg-bb-champagne transition-colors rounded-sm px-4 py-2 text-sm font-medium">My Dashboard</button>
            </>
          ) : (
            <>
              <button onClick={() => nav("/login")} data-testid="datanav-login"
                className="hidden sm:block text-sm text-bb-text/80 hover:text-bb-gold transition-colors px-2 py-2">Sign In</button>
              <button onClick={() => nav("/pricing")} data-testid="datanav-subscribe"
                className="hidden sm:block bg-bb-gold text-bb-bg hover:bg-bb-champagne transition-colors rounded-sm px-4 py-2 text-sm font-medium">Subscribe</button>
            </>
          )}
          <button onClick={() => setOpen(!open)} data-testid="datanav-hamburger" aria-label="Menu"
            className="md:hidden text-bb-text/85 hover:text-bb-gold transition-colors p-2">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-white/8 bg-bb-bg/98 backdrop-blur-md" data-testid="datanav-mobile-menu">
          <div className="px-5 py-4 space-y-1">
            {LINKS.map(([label, to, tid]) => (
              <button key={to} onClick={() => go(to)} data-testid={`m-${tid}`}
                className="block w-full text-left text-sm text-bb-text/85 hover:text-bb-gold transition-colors py-2 border-b border-white/5 uppercase tracking-[0.12em]">
                {label}
              </button>
            ))}
            <div className="flex gap-3 pt-4">
              {user ? (
                <>
                  <button onClick={() => { setOpen(false); logout(); }} className="flex-1 border border-bb-gold/50 text-bb-gold rounded-sm py-2.5 text-sm">Sign Out</button>
                  <button onClick={() => go("/dashboard")} className="flex-1 bg-bb-gold text-bb-bg rounded-sm py-2.5 text-sm font-medium">My Dashboard</button>
                </>
              ) : (
                <>
                  <button onClick={() => go("/login")} className="flex-1 border border-bb-gold/50 text-bb-gold rounded-sm py-2.5 text-sm">Sign In</button>
                  <button onClick={() => go("/pricing")} className="flex-1 bg-bb-gold text-bb-bg rounded-sm py-2.5 text-sm font-medium">Subscribe</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

export function DataFooter() {
  const nav = useNavigate();
  const cols = [
    ["Products", [["The African Dollar Wire", "/wire"], ["The Metals Desk", "/metals"], ["VDR-RDI Index & Licensing", "/vdr-rdi"], ["Price Desk · Rough & Polished", "/market"], ["Live Data Terminal", "/live-data"], ["Pricing", "/pricing"]]],
    ["Company", [["About & Methodology", "/about"], ["Data Standards", "/methodology"], ["Terms & Data Licensing", "/terms"], ["African FX Board", "/africa-fx"]]],
  ];
  return (
    <footer className="border-t border-white/5 bg-bb-surface/40" data-testid="data-footer">
      <div className="max-w-6xl mx-auto px-5 py-12">
        <div className="flex flex-wrap justify-between gap-10">
          <div className="max-w-sm">
            <DataLogo onClick={() => nav("/")} />
            <p className="text-bb-muted text-sm mt-4 leading-relaxed">
              Virtus Data is the market intelligence imprint of Virtus Registry: independent pricing,
              indices and analysis for Botswana-origin diamonds and African commodities.
            </p>
            <a href="mailto:virtusregistry@virturegistry.com" className="block text-bb-champagne hover:text-bb-gold text-xs mt-4">virtusregistry@virturegistry.com</a>
          </div>
          <div className="flex gap-16 text-sm">
            {cols.map(([title, links]) => (
              <div key={title} className="space-y-2">
                <div className="text-bb-gold uppercase text-[11px] tracking-wider mb-2">{title}</div>
                {links.map(([x, to]) => (
                  <div key={x} onClick={() => nav(to)} className="text-bb-muted hover:text-bb-gold transition-colors cursor-pointer">{x}</div>
                ))}
              </div>
            ))}
          </div>
        </div>
        <div className="border-t border-white/5 mt-10 pt-6 space-y-2 text-[0.68rem] text-bb-dim leading-relaxed">
          <p data-testid="data-footer-ai-disclosure">
            Editions are AI-assisted syntheses produced from the VDR data network and reviewed by a human editor
            on the VDR Intelligence Desk prior to publication.
          </p>
          <p data-testid="data-footer-kp-disclosure">
            Coverage is aligned with Kimberley Process compliance principles. Virtus Data is not a Kimberley Process body
            and publishes no conflict-of-origin certifications. Figures are market information, not trading advice.
          </p>
          <p>
            Registry, provenance certification and direct trading live on the full Virtus platform:&nbsp;
            <button onClick={() => goSite(mainSiteHref("/"))} data-testid="data-footer-mainsite" className="text-bb-champagne hover:text-bb-gold underline underline-offset-2">virtusregistry.com</button>
          </p>
          <div className="flex flex-wrap justify-between gap-3 pt-2 text-bb-muted">
            <span>© 2026 Virtus Registry · Virtus Data</span>
            <span>Gaborone · Richmond</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
